/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useMemo} from 'react'
import {useQuery} from 'react-query'
import {
  initialQueryState,
  PaginationState,
  WithChildren,
  Response,
} from '../../../../../_dashboard/helpers'
import {useQueryRequest} from './QueryRequestProvider'
import {getMailOrders} from '../../../../services/mailOrders'
import {
  createMailOrderResponseContext,
  MailOrder,
  initialMailOrderQueryResponse,
} from './_models'
import {buildMailOrdersListQuery} from './buildMailOrdersListQuery'

interface QueryResponseContextProps {
  dataResponse?: Response<MailOrder[]>
  refetch: () => void
  query: any
  pagination: PaginationState
  isLoading: boolean
  isError: boolean
  refetchData: () => void
}

const QueryMailOrderResponseContext = createMailOrderResponseContext<MailOrder>(
  initialMailOrderQueryResponse
)

const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const {state} = useQueryRequest()
  const apiQuery = useMemo(() => buildMailOrdersListQuery(state), [state])

  const {
    isFetching,
    refetch,
    data: dataResponse,
  } = useQuery<Response<MailOrder[]>>(
    `MAIL_ORDERS_LIST-${JSON.stringify(apiQuery)}`,
    () => getMailOrders(apiQuery),
    {
      cacheTime: 0,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      staleTime: 0,
    }
  )

  const value = {
    dataResponse,
    refetch,
    query: apiQuery,
    pagination: dataResponse?.payload?.pagination || {
      totalPages: 0,
      currentPage: 0,
      pageSize: 10,
      totalItems: 0,
    },
    isLoading: isFetching,
    isError: false,
    refetchData: refetch,
  }

  return (
    <QueryMailOrderResponseContext.Provider value={value}>
      {children}
    </QueryMailOrderResponseContext.Provider>
  )
}

const useQueryResponse = () => useContext(QueryMailOrderResponseContext)

const useQueryResponseData = () => {
  const {dataResponse} = useQueryResponse()
  if (!dataResponse) return []
  return dataResponse.data || []
}

const useQueryResponsePagination = (): PaginationState => {
  const defaultPagination: PaginationState = {
    links: [],
    ...initialQueryState,
  }

  const {dataResponse} = useQueryResponse()
  if (!dataResponse || !dataResponse.payload) {
    return defaultPagination
  }

  return {
    links: [],
    ...initialQueryState,
    ...dataResponse.payload.pagination,
  }
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
}
